import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/caesar";
import { PageHeader, Loading, Pill } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, KeyRound, ShieldAlert, Power, Copy, Check } from "lucide-react";
const ROLE_OPTS = [
  { v: "super_admin", label: "Super Admin" },
  { v: "team_member", label: "Team Member" },
  { v: "member_investor", label: "Member / Investor" },
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTS.map((r) => [r.v, r.label]));

function LinkBox({ link }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex items-center gap-2 bg-ink border border-gold/30 rounded-lg p-3">
      <span className="text-xs break-all flex-1 font-num" data-testid="generated-link">{link}</span>
      <button data-testid="copy-link" onClick={copy} className="text-gold shrink-0">{copied ? <Check size={16} /> : <Copy size={16} />}</button>
    </div>
  );
}

export default function Admin() {
  const [users, setUsers] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "team_member", two_factor_enabled: false });
  const [linkDlg, setLinkDlg] = useState(null); // {title, link}

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (uid, body, msg) => {
    setUsers((prev) => prev.map((u) => (u.user_id === uid ? { ...u, ...body } : u)));
    try { await api.put(`/admin/users/${uid}`, body); toast.success(msg || "Updated"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); await load(); }
  };

  const createUser = async () => {
    if (!form.email) return toast.error("Email required");
    try {
      const { data } = await api.post("/admin/users", form);
      const link = `${window.location.origin}/reset?token=${data.invite_token}`;
      setDlg(false);
      setForm({ email: "", name: "", role: "team_member", two_factor_enabled: false });
      setUsers((prev) => [data.user, ...(prev || [])]);
      setLinkDlg({ title: "Invitation Link", link });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create user"); }
  };

  const resetLink = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-link`);
      setLinkDlg({ title: `Reset Link — ${u.email}`, link: `${window.location.origin}/reset?token=${data.reset_token}` });
      await load();
    } catch { toast.error("Could not generate link"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove access for ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("Access removed"); await load(); }
    catch { toast.error("Failed"); }
  };

  if (!users) return <Loading label="Loading users…" />;

  const pendingCount = users.filter((u) => u.pending_approval).length;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <PageHeader testId="admin-header" title="Admin — User Management" subtitle="Create accounts, assign roles, control access and security."
        action={<button data-testid="new-user-btn" onClick={() => setDlg(true)} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold"><UserPlus size={16} /> New User</button>} />

      {pendingCount > 0 && (
        <div data-testid="pending-banner" className="bg-surface border border-samber/40 rounded-xl p-4 text-sm text-samber">
          {pendingCount} account{pendingCount === 1 ? "" : "s"} awaiting your approval.
        </div>
      )}

      <div className="space-y-3">
        {users.map((u) => {
          const lastLogin = u.login_history?.length ? u.login_history[u.login_history.length - 1] : null;
          return (
            <div key={u.user_id} data-testid={`user-row-${u.email}`} className="bg-surface border border-edge rounded-xl p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{u.name}</span>
                    {u.pending_approval
                      ? <Pill color="#F59E0B" solid>PENDING</Pill>
                      : <Pill color={u.active === false ? "#E11D48" : "#10B981"} solid>{u.active === false ? "INACTIVE" : "ACTIVE"}</Pill>}
                    {u.two_factor_enabled && <Pill color="#D4AF37">2FA ON</Pill>}
                    {u.must_reset && <Pill color="#F59E0B">MUST RESET</Pill>}
                  </div>
                  <div className="text-sm text-[#A3A3A3]">{u.email}</div>
                  <div className="text-xs text-[#5a5a5a] mt-1">
                    Last login: {u.last_login ? new Date(u.last_login).toLocaleString() : "never"}
                    {lastLogin?.ip ? ` · ${lastLogin.ip}` : ""} · {(u.login_history?.length || 0)} recent attempt(s)
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {u.pending_approval && (
                    <button data-testid={`approve-${u.email}`} onClick={() => patch(u.user_id, { active: true, pending_approval: false }, "Account approved")}
                      className="h-9 px-3 rounded-md bg-sgreen text-ink text-xs font-bold inline-flex items-center gap-1"><Check size={13} /> Approve</button>
                  )}
                  <select data-testid={`role-${u.email}`} value={u.role} onChange={(e) => patch(u.user_id, { role: e.target.value }, "Role updated")}
                    className="h-9 rounded-md bg-ink border border-edge px-2 text-xs">
                    {ROLE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                  <button data-testid={`twofa-${u.email}`} onClick={() => patch(u.user_id, { two_factor_enabled: !u.two_factor_enabled }, "2FA updated")}
                    className={`h-9 px-3 rounded-md border text-xs font-semibold ${u.two_factor_enabled ? "border-gold/50 text-gold" : "border-edge text-[#A3A3A3]"}`}>2FA {u.two_factor_enabled ? "On" : "Off"}</button>
                  <button data-testid={`active-${u.email}`} onClick={() => patch(u.user_id, { active: u.active === false }, u.active === false ? "Activated" : "Deactivated")}
                    className="h-9 px-3 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] inline-flex items-center gap-1"><Power size={13} /> {u.active === false ? "Activate" : "Deactivate"}</button>
                  <button data-testid={`require-reset-${u.email}`} onClick={() => patch(u.user_id, { must_reset: true }, "User must change password")}
                    className="h-9 px-3 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] inline-flex items-center gap-1"><ShieldAlert size={13} /> Require change</button>
                  <button data-testid={`reset-link-${u.email}`} onClick={() => resetLink(u)}
                    className="h-9 px-3 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] inline-flex items-center gap-1"><KeyRound size={13} /> Reset link</button>
                  <button data-testid={`remove-${u.email}`} onClick={() => remove(u)} className="h-9 px-3 rounded-md border border-sred/40 text-xs font-semibold text-sred">Remove</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge text-[#FAFAFA]">
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="new-user-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="new-user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-11 rounded-md bg-ink border border-edge px-3 text-sm">
              {ROLE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <input type="checkbox" checked={form.two_factor_enabled} onChange={(e) => setForm({ ...form, two_factor_enabled: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" data-testid="new-user-2fa" /> Require Two-Factor Authentication
            </label>
            <p className="text-xs text-[#5a5a5a]">An invitation link will be generated for the user to set their own password. No password is stored in plain text.</p>
            <button data-testid="create-user-submit" onClick={createUser} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Send Invitation</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkDlg} onOpenChange={(o) => !o && setLinkDlg(null)}>
        <DialogContent className="bg-surface border-edge text-[#FAFAFA]">
          <DialogHeader><DialogTitle>{linkDlg?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#A3A3A3]">Share this secure link with the user. It expires and can be used once.</p>
          {linkDlg && <LinkBox link={linkDlg.link} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
